import React, { useEffect, useState, useCallback } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { adminApiRequest } from "../utils/adminApi";
import placeholderImage from "../img/placeholder-img.png";
import Modal from "./Modal";

// Dedicated reorder view for a single leaf category. Shows ALL items in that
// category (in-stock and sold-out), lets staff drag to reorder, and persists
// the new sequence only when "Save order" is clicked.
export default function ItemReorder({ categoryId, categoryLabel, onClose, onSaved }) {
  const [items, setItems] = useState([]);
  const [originalIds, setOriginalIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await adminApiRequest({
          script: "fetch-category-items",
          data: { category_id: categoryId },
        });
        if (!res?.success || !Array.isArray(res.data)) {
          throw new Error(res?.error || "Failed to load items");
        }
        if (!isMounted) return;
        setItems(res.data);
        setOriginalIds(res.data.map((i) => i.id));
      } catch (err) {
        if (!isMounted) return;
        setError(err?.message || "Failed to load items");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [categoryId]);

  const hasChanges =
    items.map((i) => i.id).join(",") !== originalIds.join(",");

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    setItems((prev) => {
      const reordered = Array.from(prev);
      const [moved] = reordered.splice(result.source.index, 1);
      reordered.splice(result.destination.index, 0, moved);
      return reordered;
    });
  };

  const handleSave = async () => {
    if (saving || !hasChanges) return;
    setSaving(true);
    setError("");
    try {
      const res = await adminApiRequest({
        script: "update-item-order",
        data: {
          category_id: categoryId,
          item_ids: items.map((i) => i.id),
        },
      });
      if (!res?.success) {
        throw new Error(res?.error || "Failed to save order");
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || "Failed to save order");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = useCallback(() => {
    if (saving) return;
    if (hasChanges) {
      setShowDiscardConfirm(true);
      return;
    }
    onClose?.();
  }, [saving, hasChanges, onClose]);

  return (
    <div className="fixed inset-0 z-[9998] flex flex-col bg-slate-100">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-4 md:px-6">
        <div className="min-w-0">
          <h2 className="truncate text-lg font-semibold text-slate-900 md:text-xl">
            Reorder: {categoryLabel}
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Drag items into the order they should appear on the website.
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className={`inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white transition ${
              saving || !hasChanges
                ? "cursor-not-allowed bg-slate-300"
                : "bg-emerald-600 hover:bg-emerald-700"
            }`}
          >
            {saving && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"></span>
            )}
            {saving ? "Saving..." : "Save order"}
          </button>
        </div>
      </div>

      {error && (
        <div className="border-b border-rose-200 bg-rose-50 px-4 py-2 text-sm font-medium text-rose-700 md:px-6">
          {error}
        </div>
      )}

      {/* List */}
      <div className="flex-1 overflow-y-auto px-4 py-4 md:px-6">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-300 border-t-slate-800"></div>
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center text-sm text-slate-500">
            No items in this category.
          </div>
        ) : (
          <div className="mx-auto max-w-2xl">
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="reorder-list">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="space-y-2"
                  >
                    {items.map((item, index) => {
                      const isSoldOut = Number(item.in_stock) === 0;
                      return (
                        <Draggable
                          key={item.id}
                          draggableId={String(item.id)}
                          index={index}
                        >
                          {(prov, snapshot) => (
                            <div
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              {...prov.dragHandleProps}
                              className={`flex items-center gap-3 rounded-2xl border bg-white p-3 shadow-sm transition ${
                                snapshot.isDragging
                                  ? "border-emerald-400 shadow-lg"
                                  : "border-slate-200"
                              }`}
                              style={prov.draggableProps.style}
                            >
                              <div className="flex h-8 w-8 shrink-0 items-center justify-center text-slate-400">
                                <i className="fa fa-grip-vertical"></i>
                              </div>

                              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-xs font-semibold text-slate-600">
                                {index + 1}
                              </div>

                              <img
                                src={
                                  item.image
                                    ? `https://lasgidirestaurant.com/${item.image}`
                                    : placeholderImage
                                }
                                alt={item.item_name}
                                className="h-12 w-12 shrink-0 rounded-xl border border-slate-200 object-cover"
                              />

                              <div className="min-w-0 flex-1">
                                <p className="truncate font-medium text-slate-900">
                                  {item.item_name}
                                </p>
                                <p className="text-xs text-slate-500">
                                  £{parseFloat(item.price || 0).toFixed(2)}
                                </p>
                              </div>

                              {isSoldOut && (
                                <span className="shrink-0 rounded-full bg-rose-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-rose-700">
                                  Sold Out
                                </span>
                              )}
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        )}
      </div>

      {showDiscardConfirm && (
        <Modal
          title="Discard changes?"
          onClose={() => setShowDiscardConfirm(false)}
          onSubmit={(e) => {
            e.preventDefault();
            setShowDiscardConfirm(false);
            onClose?.();
          }}
          showCancel={true}
          showSubmitButton={true}
          submitButtonText="Discard"
          submitButtonClass="bg-rose-600 hover:bg-rose-700 text-white"
        >
          <div className="py-2 text-sm text-slate-600">
            You have unsaved order changes. Are you sure you want to discard
            them?
          </div>
        </Modal>
      )}
    </div>
  );
}
